import { queryOptions, useQuery } from '@tanstack/react-query';

import type { TimeRange } from '@cast/shared-types';
import { QueryOptions } from '@cast/shared-types';
import { DbQuery } from '@cast/types';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

const apiFn = apiClient.dbo.getCacheQuery;

export type UseCacheQueryQueryArgs<SELECT> = QueryOptions<DbQuery, SELECT> & {
  cacheGroupId?: string;
  cacheId?: string;
  templateHash?: string;
  key: string;
  getRange: () => TimeRange;
};

const DBO_CACHE_QUERY_KEY = 'dbo-cache-query';

const getCacheQueryQueryKey = ({
  cacheGroupId,
  cacheId,
  templateHash,
  key,
}: {
  cacheGroupId?: string;
  cacheId?: string;
  templateHash?: string;
  key?: string;
} = {}) =>
  [DBO_CACHE_QUERY_KEY, cacheGroupId, cacheId, templateHash, key] as const;

const getCacheQueryQueryOptions = ({
  cacheGroupId,
  cacheId,
  templateHash,
  enabled,
  key,
  getRange,
}: {
  cacheGroupId?: string;
  cacheId?: string;
  templateHash?: string;
  key: string;
  getRange: () => TimeRange;
  enabled?: boolean;
}) =>
  queryOptions({
    queryKey: getCacheQueryQueryKey({
      cacheGroupId,
      cacheId,
      templateHash,
      key,
    }),
    queryFn: async () => {
      const range = getRange();
      const { data } = await apiFn({
        groupId: cacheGroupId!,
        cacheId: cacheId!,
        templateHash: templateHash!,
        metricsRangeStart: range.from,
        metricsRangeEnd: range.to,
      });
      return data;
    },
    enabled: makeIsEnabled(!!cacheGroupId, !!cacheId, !!templateHash, enabled),
  });

export const useCacheQueryQuery = <SELECT = DbQuery>({
  cacheGroupId,
  cacheId,
  templateHash,
  enabled,
  key,
  getRange,
  retry,
  refetchInterval,
}: UseCacheQueryQueryArgs<SELECT>) => {
  return useQuery({
    ...getCacheQueryQueryOptions({
      cacheGroupId,
      cacheId,
      templateHash,
      enabled,
      key,
      getRange,
    }),
    retry,
    refetchInterval,
  });
};
